import * as Dialog from '@radix-ui/react-dialog'
import { useCallback, useEffect, useState } from 'react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

import { DrawerState, ExpandableDrawerProps } from './types'
import './styles.scss'
import { ReactSVG } from 'react-svg'
import { Utils } from '@Util/utils'
import { CloseButton } from '@Components/close-button/close-button'

const CLASS_NAME = 'expandable-drawer'
const MAX_APP_HEIGHT = 1024

type DragState = {
  x: number
  y: number
  timestamp: number
}

type DrawerPositionDetails = {
  top: number
  height: number
  relativePosition: number
}

export const ExpandableDrawer = (props: ExpandableDrawerProps) => {
  // Defaulting undefined to true
  const scrollable = props.scrollable !== false

  const [dragStart, setDragStart] = useState<DragState | null>(null)
  const [dragEnd, setDragEnd] = useState<DragState | null>(null)
  const [fullScreen, setFullScreen] = useState<boolean>(false)
  const [contentParent, setContentParent] = useState<HTMLDivElement | null>(null)
  const [contentHeader, setContentHeader] = useState<HTMLDivElement | null>(null)
  const [contentBody, setContentBody] = useState<HTMLDivElement | null>(null)

  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight)

  const useRefCallback = (setter: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>) => {
    return useCallback(
      (node: HTMLDivElement) => {
        setter(node)
      },
      [setter],
    )
  }

  const getUpdatedDrawerDetails = useCallback(
    (dragPosY: number): DrawerPositionDetails => {
      const maxAppHeight = MAX_APP_HEIGHT
      const topOfAppScreen = Math.max((windowHeight - maxAppHeight) / 2, 0)
      const drawerHeight = Math.min(windowHeight - topOfAppScreen - dragPosY, maxAppHeight)
      const drawerTop = Math.max(topOfAppScreen, dragPosY)

      const relativePosition =
        1 - (dragPosY - topOfAppScreen) / Math.min(windowHeight, maxAppHeight)

      return {
        top: drawerTop,
        height: drawerHeight,
        relativePosition,
      }
    },
    [windowHeight],
  )

  const updateSize = useCallback(
    (topY: number) => {
      if (contentParent) {
        const newStylings = getUpdatedDrawerDetails(topY)
        contentParent.style.top = `${newStylings.top}px`
        contentParent.style.height = `${newStylings.height}px`
      }
    },
    [contentParent, getUpdatedDrawerDetails],
  )

  const resizeEventHandler = useCallback(() => {
    setWindowHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', resizeEventHandler)

    return () => {
      window.removeEventListener('resize', resizeEventHandler)
    }
  }, [resizeEventHandler])

  useEffect(() => {
    setFullScreen(false)
    setDragStart(null)
    setDragEnd(null)
  }, [props.state])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setDragEnd(null)
    setDragStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
      timestamp: Date.now(),
    })
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setDragEnd(null)
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      timestamp: Date.now(),
    })
  }, [])

  const handleDragMove = useCallback(
    (positionX: number, positionY: number) => {
      setDragEnd({
        x: positionX,
        y: positionY,
        timestamp: Date.now(),
      })
      updateSize(positionY)
    },
    [updateSize],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const newY = e.targetTouches[0].clientY > 0 ? e.targetTouches[0].clientY : 0
      handleDragMove(e.targetTouches[0].clientX, newY)
    },
    [handleDragMove],
  )

  const handleSelectEnd = useCallback(() => {
    if (!dragStart || !dragEnd) {
      setDragStart(null)
      setDragEnd(null)
      return
    }

    const deltaY = dragEnd.y - dragStart.y
    const deltaTime = dragEnd.timestamp - dragStart.timestamp
    const velocityY = deltaY / deltaTime

    const positionDetails = getUpdatedDrawerDetails(dragEnd.y)

    if (velocityY > 0.75) {
      props.onOpenChange(false)
    }

    if (velocityY < -0.75) {
      updateSize(0)
      setFullScreen(true)
    }

    if (positionDetails.relativePosition < 0.35) {
      props.onOpenChange(false)
    }

    if (contentParent && positionDetails.relativePosition > 0.98) {
      updateSize(0)
      setFullScreen(true)
    }

    setDragStart(null)
    setDragEnd(null)
  }, [props, dragEnd, dragStart, updateSize, getUpdatedDrawerDetails, contentParent])

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragStart === null) {
        return
      }

      const mouseBuffer = 30
      const newY = e.clientY - mouseBuffer > 0 ? e.clientY - mouseBuffer : 0
      handleDragMove(e.clientX, newY)
    },
    [dragStart, handleDragMove],
  )

  const renderHeader = useCallback(
    (refSetter: (node: HTMLDivElement) => void) => {
      if (fullScreen) {
        return (
          <>
            <div className={`${CLASS_NAME}__full-screen-header`}>
              <h3>{props.drawerName || 'Settings'}</h3>
              <Dialog.Close asChild className={`${props.classPrefix}__close ${CLASS_NAME}__close`}>
                <CloseButton onClick={() => {}} />
              </Dialog.Close>
            </div>
          </>
        )
      }

      return (
        <>
          <div
            className={`${props.classPrefix}__swipeable-area ${CLASS_NAME}__swipeable-area`}
            onMouseDown={scrollable ? handleMouseDown : Utils.noop}
            onTouchStart={scrollable ? handleTouchStart : Utils.noop}
            onTouchMove={scrollable ? handleTouchMove : Utils.noop}
            onTouchEnd={scrollable ? handleSelectEnd : Utils.noop}>
            {scrollable ? (
              <hr
                className={`${props.classPrefix}__horizontal-bar ${CLASS_NAME}__horizontal-line`}
              />
            ) : null}
          </div>
          {props.hasCloseButton ? (
            <Dialog.Close asChild className={`${props.classPrefix}__close ${CLASS_NAME}__close`}>
              <CloseButton onClick={() => {}} backgroundColor="white" />
            </Dialog.Close>
          ) : null}
          {props.drawerIconUrl ? (
            <div ref={refSetter}>
              <ReactSVG className={`${props.classPrefix}__title`} src={props.drawerIconUrl} />
            </div>
          ) : props.drawerName ? (
            <div ref={refSetter} className={`${props.classPrefix}__title`}>
              {props.drawerName}
            </div>
          ) : null}
        </>
      )
    },
    [
      fullScreen,
      handleSelectEnd,
      handleTouchMove,
      handleTouchStart,
      handleMouseDown,
      props.classPrefix,
      scrollable,
      props.drawerName,
      props.drawerIconUrl,
      props.hasCloseButton,
    ],
  )

  useEffect(() => {
    if (props.state === DrawerState.OPEN && !fullScreen) {
      const defaultOpenHeight = windowHeight / 2

      // If we're rendering a non scrollable drawer and the content is cut off (plus a few pixel padding), change the default opening
      // height to be bigger
      if (!scrollable && contentBody) {
        const appHeight = Math.min(MAX_APP_HEIGHT, windowHeight)
        const padding = 16
        const drawerHeight = contentBody.scrollHeight + (contentHeader?.scrollHeight ?? 0) + padding
        const topOfAppScreen = window.innerHeight - appHeight
        // Smaller number moves futher towards the top of the screen
        updateSize(appHeight - drawerHeight + topOfAppScreen / 2)
      } else {
        updateSize(defaultOpenHeight)
      }
    }
  }, [
    props.state,
    scrollable,
    fullScreen,
    updateSize,
    contentParent,
    contentBody,
    contentHeader,
    windowHeight,
  ])

  const contentClassname = scrollable ? 'scrollable' : 'fixed'

  return (
    <Dialog.Portal>
      <VisuallyHidden.Root>
        <Dialog.Description>{`${props.drawerName} Drawer`}</Dialog.Description>
      </VisuallyHidden.Root>
      <Dialog.Overlay
        className={`${props.classPrefix}__overlay ${CLASS_NAME}__overlay`}
        data-state={props.state.toLowerCase()}
        onMouseMove={dragStart ? onMouseMove : () => {}}
        onMouseUp={handleSelectEnd}
      />

      <div
        className={`${props.classPrefix}__content-parent ${CLASS_NAME}__content-parent`}
        data-state={fullScreen ? 'full-screen' : 'modal'}
        onMouseMove={dragStart ? onMouseMove : () => {}}
        onMouseUp={handleSelectEnd}
        ref={useRefCallback(setContentParent)}>
        <div className={`${props.classPrefix}__content-wrapper ${CLASS_NAME}__content-wrapper`}>
          <Dialog.Content
            onOpenAutoFocus={props.onOpenAutoFocus ?? Utils.noop}
            className={`${props.classPrefix}__content ${CLASS_NAME}__content  ${props.state === DrawerState.CLOSING ? 'closing' : ''}`}>
            <VisuallyHidden.Root>
              <Dialog.Title>Settings</Dialog.Title>
            </VisuallyHidden.Root>
            {renderHeader(useRefCallback(setContentHeader))}
            <div
              ref={useRefCallback(setContentBody)}
              className={`${props.classPrefix}__${contentClassname}-content ${CLASS_NAME}__${contentClassname}-content`}>
              {props.children}
            </div>
          </Dialog.Content>
        </div>
      </div>
    </Dialog.Portal>
  )
}
